import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useAppSelector } from "hooks/storeHooks"
import { selectUserInfo } from "store/user/userSlice"
import { tintVTOService } from "services/tintvto"
import { useBillingPortal } from "hooks/useBillingPortal"
import { CreditCard, Loader2, LogOut, Settings } from "lucide-react"
import { cn } from "@/lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

const MAX_USERNAME_LENGTH = 20

interface DemoUserButtonProps {
  collapsed?: boolean
}

export function DemoUserButton({ collapsed = false }: DemoUserButtonProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const userInfo = useAppSelector(selectUserInfo)
  const { canManageBilling, isPortalLoading, openBillingPortal } = useBillingPortal()

  const displayName = userInfo.displayName?.trim() || userInfo.username || t("nav.demoUser")
  const displayUsername =
    displayName.length > MAX_USERNAME_LENGTH
      ? `${displayName.substring(0, MAX_USERNAME_LENGTH)}...`
      : displayName
  const firstLetter = displayName.charAt(0).toUpperCase() || "D"

  const onLogout = () => {
    tintVTOService.logout()
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="user-menu-trigger"
          className={cn(
            "flex items-center gap-2.5 rounded-md text-left text-[13px] text-foreground/70 transition-colors hover:bg-accent hover:text-foreground",
            collapsed ? "justify-center p-2" : "w-full px-2.5 py-[7px]"
          )}
        >
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-400 to-violet-400 text-[10px] font-semibold text-white">
            {firstLetter}
          </div>
          {!collapsed && <span className="truncate">{displayUsername}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="user-menu"
        side={collapsed ? "right" : "top"}
        align="start"
        sideOffset={8}
        className="w-48 p-1"
      >
        <button
          data-testid="user-menu-settings"
          onClick={() => navigate("/user-settings")}
          className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-accent"
        >
          <Settings className="size-3.5 opacity-60" />
          {t("userSettingsPage.userSettings")}
        </button>
        {canManageBilling && (
          <button
            data-testid="user-menu-billing"
            onClick={() => void openBillingPortal()}
            disabled={isPortalLoading}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isPortalLoading ? (
              <Loader2 className="size-3.5 animate-spin opacity-60" />
            ) : (
              <CreditCard className="size-3.5 opacity-60" />
            )}
            {t("userSettingsPage.manageBilling", "Manage plan")}
          </button>
        )}
        <div className="my-1 h-px bg-border" />
        <button
          data-testid="user-menu-logout"
          onClick={onLogout}
          className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive transition-colors hover:bg-destructive/10"
        >
          <LogOut className="size-3.5" />
          {t("userSettingsPage.logout")}
        </button>
      </PopoverContent>
    </Popover>
  )
}
